import React, { useState } from "react";
import "./SectionOne.scss";
import { Modal } from "semantic-ui-react";
// Adjust this relative path to wherever the video actually lives in your project.
import ecfmTeaser from "../../../resources/eaciit_hyperscaling_epm.mp4";

/**
 * Icon set
 * Rendered as inline SVG so stroke color, weight and sizing are 100%
 * controlled by CSS (no raster/PNG scaling artifacts across DPR).
 */
const LayersIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M12 3.5L21 8L12 12.5L3 8L12 3.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M3 12L12 16.5L21 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3 16L12 20.5L21 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const TrendUpIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M3 17L9.5 10.5L14 15L21 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15 7H21V13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const PeopleIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.8" />
        <path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="17" cy="8.5" r="2.3" stroke="currentColor" strokeWidth="1.8" />
        <path d="M15.5 13.8c2.6.2 4.5 2.1 4.5 4.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

const PlayIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.6" />
        <path d="M10 8.5L16 12L10 15.5V8.5Z" fill="currentColor" />
    </svg>
);

/** Left column content: capability rows under the headline. */
const CAPABILITY_ROWS = [
    {
        id: "connect",
        Icon: LayersIcon,
        title: "A Single Intelligent Operating Layer Connecting",
        detail: "Data • AI • Planning • Workflow • Execution",
    },
    {
        id: "improve",
        Icon: TrendUpIcon,
        title: "To Continuously Improve",
        detail: "Sales • Operational Performance • EBITDA • Working Capital • FCF • Enterprise Value",
    },
];

/** Right column content: highlight rows inside the lavender panel. */
const HIGHLIGHT_ROWS = [
    {
        id: "value",
        Icon: TrendUpIcon,
        lead: "Transforms Data into",
        emphasis: "Measurable Business Value",
    },
    {
        id: "unify",
        Icon: PeopleIcon,
        lead: "Unifies Siloed Capabilities",
        emphasis: "across the Organization",
    },
];

const SectionOne = () => {
    const [open, setOpen] = useState(false);

    const handleScheduleDemo = () => {
        window.location = "/contact";
    };

    return (
        <section className="epm-hero">
            <div className="epm-hero__container">
                {/* ---------- Left column ---------- */}
                <div className="epm-hero__column epm-hero__column--left">
                    <div className="epm-hero__brand">
                        <span className="epm-hero__brand-mark">
                            <span className="epm-hero__brand-accent">EPM</span>
                            <span className="epm-hero__brand-dark">Tech</span>
                        </span>
                        <span className="epm-hero__brand-rule" aria-hidden="true" />
                    </div>

                    <h1 className="epm-hero__headline epm-hero__headline--accent">
                        The AI-Native Enterprise Performance Management Platform
                    </h1>

                    <div className="epm-hero__capabilities">
                        {CAPABILITY_ROWS.map((row) => (
                            <div className="epm-hero__capability" key={row.id}>
                                <span className="epm-hero__icon-badge">
                                    <row.Icon />
                                </span>
                                <div className="epm-hero__capability-text">
                                    <p className="epm-hero__capability-title">{row.title}</p>
                                    <p className="epm-hero__capability-detail">{row.detail}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ---------- Right column ---------- */}
                <div className="epm-hero__column epm-hero__column--right">
                    <div className="epm-hero__right-container">
                      <h2 className="epm-hero__headline">
                          <span className="epm-hero__headline-main">
                              Run Your Enterprise
                              <br />
                              with Real-Time
                          </span>{" "}
                          <span className="epm-hero__headline-accent">Decision Intelligence</span>
                      </h2>

                      <div className="epm-hero__panel">
                          {HIGHLIGHT_ROWS.map((row) => (
                              <div className="epm-hero__highlight" key={row.id}>
                                  <span className="epm-hero__icon-badge epm-hero__icon-badge--circle">
                                      <row.Icon />
                                  </span>
                                  <p className="epm-hero__highlight-text">
                                      <span className="epm-hero__highlight-lead">{row.lead}</span>
                                      <span className="epm-hero__highlight-emphasis">{row.emphasis}</span>
                                  </p>
                              </div>
                          ))}
                      </div>
                    </div>
                </div>

                <p className="epm-hero__impact-line">
                    From Periodic Performance Management to Continuous Enterprise Optimization
                </p>

                <div className="epm-hero__actions">
                    <button
                        type="button"
                        className="epm-hero__button epm-hero__button--primary"
                        onClick={handleScheduleDemo}
                    >
                        Schedule Demo
                    </button>
                    <Modal
                        size="large"
                        closeIcon
                        onClose={() => setOpen(false)}
                        onOpen={() => setOpen(true)}
                        open={open}
                        trigger={
                            <button
                                type="button"
                                className="epm-hero__button epm-hero__button--dark"
                            >
                                Watch Video
                                <span className="epm-hero__button-icon">
                                    <PlayIcon />
                                </span>
                            </button>
                        }
                    >
                        <Modal.Content image>
                            <video
                                className="eaciit-video-player"
                                playsInline
                                autoPlay
                                controls
                            >
                                <source src={ecfmTeaser} type="video/mp4" />
                                Your browser does not support HTML video.
                            </video>
                        </Modal.Content>
                    </Modal>
                </div>
            </div>
        </section>
    );
};

export default SectionOne;